// src/supplier/context/SupplierAuthContext.jsx
import { createContext, useContext, useEffect, useMemo, useState } from "react";

const SupplierAuthContext = createContext(null);
export const SUPPLIER_SESSION_KEY = "supplier_session";

function safeParse(raw) {
  try { return JSON.parse(raw); } catch { return null; }
}

function readSession() {
  const raw = localStorage.getItem(SUPPLIER_SESSION_KEY);
  return raw ? safeParse(raw) : null;
}

function writeSession(session) {
  localStorage.setItem(SUPPLIER_SESSION_KEY, JSON.stringify(session));
}

function clearSession() {
  localStorage.removeItem(SUPPLIER_SESSION_KEY);
}

export function SupplierAuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const syncSession = () => {
    const restored = readSession();
    setSession(restored);
    setLoading(false);
  };

  useEffect(() => {
    syncSession();
    const onStorage = (e) => { if (e.key === SUPPLIER_SESSION_KEY) syncSession(); };
    const onFocus = () => syncSession();
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const login = async (data) => {
    // data = { token, user, suppliers }
    const next = { token: data.token, user: data.user, suppliers: data.suppliers || [] };
    writeSession(next);
    setSession(next);
    return data;
  };

  const logout = () => {
    clearSession();
    setSession(null);
  };

  const value = useMemo(() => ({
    loading,
    session,
    token: session?.token || null,
    user: session?.user || null,
    suppliers: session?.suppliers || [],
    isAuthenticated: Boolean(session?.token),
    login,
    logout,
    syncSession,
  }), [session, loading]);

  return <SupplierAuthContext.Provider value={value}>{children}</SupplierAuthContext.Provider>;
}

export function useSupplierAuth() {
  const ctx = useContext(SupplierAuthContext);
  if (!ctx) throw new Error("useSupplierAuth must be used inside SupplierAuthProvider");
  return ctx;
}
